// @mui
import { Button, Stack, Table, TableBody, TableContainer } from "@mui/material";
// routes

// _mock_
// components
import Scrollbar from "../../../components/scrollbar";
import {
  emptyRows,
  getComparator,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  useTable,
} from "../../../components/table";
// sections
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { getLoggedInUser } from "src/auth/utils";
import ReportController from "src/controller/Report.controller";
import { ReportTableRow } from "../list";
import StudentTabContainer from "../sections/StudentTabContainer.section";
import AddReportDialog from "./AddReportDialog";

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: "report", label: "Report", align: "left" },
  { id: "program", label: "Program", align: "left" },
  { id: "teacher", label: "Teacher", align: "left" },
  { id: "date", label: "Date And Time", align: "left" },
  { id: "shared", label: "Shared", align: "left" },
  { id: "pdfReport", label: "Report PDF", align: "left" },
];

// ----------------------------------------------------------------------
let LearningPlans = null;

export default function ReportPage({ enrollments, student, hideAdd = false }) {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    onSelectRow,
    onSelectAllRows,
    onSort,

    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({
    defaultOrderBy: "createdOn",
    defaultOrder: "desc",
  });

  const params = useParams();
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [queryInput, setQueryInput] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [id, setId] = useState(null);

  const [pagination, setPagination] = useState({
    totalPages: 0,
    totalRows: 0,
    currentPage: 0,
  });
  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(order, orderBy),
    filterName,
    filterStatus,
  });

  const dataInPage = dataFiltered?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const denseHeight = dense ? 52 : 72;

  const isFiltered = filterName !== "" || filterStatus !== "all";

  const isNotFound =
    (!dataFiltered?.length && !!filterName) ||
    (!dataFiltered?.length && !!filterStatus);

  const handleReport = () => {
    setReportOpen(true);
  };

  const handleReportClose = () => {
    setReportOpen(false);
    fetchRecords(); // Fetch the latest assessments
  };
  function fetchRecords(search = null, page = 0) {
    if (!id) return;
    let query = "?";

    if (search !== null) {
      query += `search=${search}&`;
    }

    query += `page=${page}&limit=${rowsPerPage}`;

    setIsLoading(true);
    ReportController.student(id, query)
      .then((res) => {
        setPagination({
          totalPages: res.totalPages,
          totalRows: res.totalRecords,
          currentPage: res.currentPage,
        });
        setTableData(res.data);
      })
      .catch((error) => {
        setTableData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(fetchRecords, []);

  useEffect(() => {
    if (queryInput !== "") {
      const i = setTimeout(() => fetchRecords(queryInput), 0);
      return () => {
        clearTimeout(i);
      };
    } else {
      fetchRecords();
    }
  }, [queryInput, id]);

  useEffect(() => {
    if (getLoggedInUser()?.role === "student") {
      setId(getLoggedInUser()?.student_id);
    } else {
      setId(params.id);
    }
  }, []);

  return (
    <StudentTabContainer
      title="Report"
      content={
        !hideAdd && (
          <Stack
            spacing={2}
            display="flex"
            justifyContent="space-between"
            direction="row"
          >
            <Button Button variant="contained" onClick={handleReport}>
              Add Report
            </Button>
          </Stack>
        )
      }
    >
      <TableContainer sx={{ position: "relative", overflow: "unset" }}>
        <Scrollbar>
          <Table size={dense ? "small" : "medium"} sx={{ minWidth: 800 }}>
            <TableHeadCustom
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={tableData?.length}
              numSelected={selected?.length}
              onSort={onSort}
            />

            <TableBody>
              {!isLoading &&
                dataFiltered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <ReportTableRow
                      key={row.id}
                      row={row}
                      selected={selected.includes(row.id)}
                      onSelectRow={() => onSelectRow(row.id)}
                    />
                  ))}

              <TableEmptyRows
                height={denseHeight}
                emptyRows={emptyRows(page, rowsPerPage, tableData.length)}
              />

              <TableNoData isNotFound={isNotFound} />
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>

      <TablePaginationCustom
        count={dataFiltered?.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        dense={dense}
        onChangeDense={onChangeDense}
      />
      {!hideAdd && (
        <AddReportDialog
          open={reportOpen}
          onClose={handleReportClose}
          enrollments={enrollments}
        />
      )}
    </StudentTabContainer>
  );
}

// ============================================
function applyFilter({
  inputData,
  comparator,
  filterName,
  filterStatus,
  filterRole,
}) {
  const stabilizedThis = inputData?.map((el, index) => [el, index]);

  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis?.map((el) => el[0]);

  if (filterName) {
    inputData = inputData?.filter(
      (user) => user.name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterStatus !== "all") {
    inputData = inputData.filter((user) => user.status === filterStatus);
  }

  if (filterRole !== "all") {
    inputData = inputData?.filter((user) => user?.role === filterRole);
  }

  return inputData;
}
